import { uid } from "@dhx/ts-common/core";
import { toNode } from "@dhx/ts-common/html";
import { IMessageProps, MessageContainerPosition, IMessageContainerInfo } from "./types";

const nodeTimeout: WeakMap<HTMLElement, any> = new WeakMap();
const containers: Map<HTMLElement, IMessageContainerInfo> = new Map();

function createMessageContainer(parent, position) {
	const messageContainer = document.createElement("div");
	messageContainer.setAttribute("data-position", position);
	messageContainer.className =
		"dhx_message-container " +
		"dhx_message-container--" +
		position +
		(parent === document.body ? " dhx_message-container--in-body" : "");
	return messageContainer;
}

function onExpire(node: HTMLElement, fromClick?: boolean) {
	if (fromClick) {
		clearTimeout(nodeTimeout.get(node));
	}
	const container = node.parentNode as HTMLElement;
	const position = container.getAttribute("data-position") as MessageContainerPosition;
	const parent = container.parentNode as HTMLElement;
	const messageContainerInfo = containers.get(parent);
	if (!messageContainerInfo) {
		return;
	}
	const positionInfo = messageContainerInfo[position];
	if (!positionInfo) {
		return;
	}

	const stack = positionInfo.stack;
	const index = stack.indexOf(node);
	if (index !== -1) {
		container.removeChild(node);
		stack.splice(index, 1);
		if (stack.length === 0) {
			parent.removeChild(container);
		}
		return;
	}
}

export function message(props: string | IMessageProps) {
	if (typeof props === "string") {
		props = { text: props };
	}
	props.position = props.position || MessageContainerPosition.topRight;

	const messageBox = document.createElement("div");
	messageBox.className = "dhx_widget dhx_message " + (props.css || "");
	messageBox.setAttribute("role", "alert");
	const textId = props.text && uid();
	textId && messageBox.setAttribute("aria-describedby", textId);
	if (props.html) {
		messageBox.innerHTML = props.html;
	} else {
		messageBox.innerHTML = `<span class="dhx_message__text" id=${textId}>${props.text}</span>
		${props.icon ? `<span class="dhx_message__icon dxi ${props.icon}"></span>` : ""}`;
	}

	const parent = props.node ? toNode(props.node) : document.body;
	const position = getComputedStyle(parent).position;
	if (position === "static") {
		parent.style.position = "relative";
	}
	const messageContainerInfo = containers.get(parent);
	if (!messageContainerInfo) {
		containers.set(parent, {
			[props.position]: {
				stack: [],
				container: createMessageContainer(parent, props.position),
			},
		});
	} else if (!messageContainerInfo[props.position]) {
		messageContainerInfo[props.position] = {
			stack: [],
			container: createMessageContainer(parent, props.position),
		};
	}
	const { stack, container } = containers.get(parent)[props.position];

	if (stack.length === 0) {
		parent.appendChild(container);
	}
	stack.push(messageBox);
	container.appendChild(messageBox);
	if (props.expire) {
		const timeout = setTimeout(() => onExpire(messageBox), props.expire);
		nodeTimeout.set(messageBox, timeout);
	}
	messageBox.onclick = () => onExpire(messageBox, true);
}
